import { combineReducers, configureStore } from "@reduxjs/toolkit";
import storage from "redux-persist/lib/storage";
import {
  createTransform,
  PersistConfig,
  persistReducer,
  persistStore,
} from "redux-persist";
import {
  FLUSH,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
  REHYDRATE,
} from "redux-persist/es/constants";
import { AuthState } from "../../types/rtk/auth/login";

import { apiSlice } from "../_api/api-slice";
import ThemeReducer from "../theme/theme";
import AuthReducer from "../auth/auth";
import BasketReducer from "../basket/basket";
import SettingsReducer from "../settings/settings";
import OffersReducer from "../offers/offers-slice";
import TrackerReducer from "../tracker/tracker-slice";

// Storage with expiration (1 hour)
const storageWithExpiration = {
  ...storage,
  async getItem(key: string) {
    const item = await storage.getItem(key);
    if (!item) return null;
    
    const timestampKey = `${key}_timestamp`;
    const timestamp = await storage.getItem(timestampKey);
    const expirationTime = 60 * 60 * 1000; // 1 hour
    
    if (timestamp && Date.now() - parseInt(timestamp, 10) > expirationTime) {
      await storage.removeItem(key);
      await storage.removeItem(timestampKey);
      return null;
    }
    
    return item;
  },
  async setItem(key: string, value: string) {
    const timestampKey = `${key}_timestamp`;
    await storage.setItem(timestampKey, Date.now().toString());
    return storage.setItem(key, value);
  }
};

const combineSlices = combineReducers({
  [apiSlice.reducerPath]: apiSlice.reducer,
  theme: ThemeReducer,
  auth: AuthReducer,
  basket: BasketReducer,
  settings: SettingsReducer,
  offers: OffersReducer,
  tracker: TrackerReducer,
});

// Never persist the ephemeral apiv5 access token. It is short-lived (and
// belongs to one user/session); a persisted token would be re-sent on a later
// load — potentially stale or for a different user — yielding a 403 that does
// not self-heal. The durable `delegation_token` IS persisted, so the API layer
// re-derives a fresh access token on demand.
const authPersistTransform = createTransform<AuthState, AuthState>(
  (inboundState) => ({ ...inboundState, token: undefined }),
  (outboundState) => outboundState,
  { whitelist: ["auth"] }
);

// `offers` is never persisted either. It holds the server catalogue, whose
// prices are resolved per user (the payment API picks the price row matching
// `user.country`, so the same offer is 14 EUR, 15 CHF or 13 GBP). Persisting it
// for an hour means a catalogue fetched for one session can be replayed for
// another user, or survive a price change — and the screens read this slice, not
// the query result, so the stale amount is what customers actually see.
const persistConfig: PersistConfig<ReturnType<typeof combineSlices>> = {
  key: "root",
  storage: storageWithExpiration,
  blacklist: ["api", "offers"],
  transforms: [authPersistTransform],
};

const persistedReducer = persistReducer(persistConfig, combineSlices);

const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }).concat(apiSlice.middleware),
});

export const persistedStore = persistStore(store);
export default store;
